import React from "react";
import { LESSONS } from "@/data/lessons";
import { isLessonUnlocked, type UserProgress } from "@/lib/storage";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface LessonSelectProps {
  progress: UserProgress;
  onSelectLesson: (lessonId: number) => void;
  onLogout: () => void;
}

export default function LessonSelect({ progress, onSelectLesson, onLogout }: LessonSelectProps) {
  return (
    <div className="min-h-screen p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Learn Typing Fun</h1>
          <p className="text-muted-foreground">Welcome back, {progress.name}!</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onLogout}>
          Switch User
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LESSONS.map((lesson) => {
          const unlocked = isLessonUnlocked(lesson.id, progress);
          const completed = progress.completedLessons.includes(lesson.id);
          const stats = progress.lessonStats[lesson.id];

          return (
            <Card
              key={lesson.id}
              className={cn(
                "transition-all",
                !unlocked && "opacity-50",
                unlocked && !completed && "border-primary/30",
                completed && "border-green-500/30",
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {lesson.id}. {lesson.title}
                  </CardTitle>
                  {completed && <span className="text-green-500 text-sm font-bold">Done</span>}
                  {!unlocked && <span className="text-muted-foreground text-sm">Locked</span>}
                </div>
                <CardDescription>{lesson.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1 mb-3">
                  {lesson.newKeys
                    .filter((k) => k.length === 1)
                    .map((key) => (
                      <kbd
                        key={key}
                        className="px-1.5 py-0.5 rounded border bg-muted text-xs font-mono"
                      >
                        {key}
                      </kbd>
                    ))}
                </div>

                {stats && (
                  <div className="text-xs text-muted-foreground mb-3">
                    Best: {stats.bestWpm} WPM / {stats.bestAccuracy}% accuracy
                  </div>
                )}

                <Button
                  className="w-full"
                  disabled={!unlocked}
                  variant={completed ? "outline" : "default"}
                  onClick={() => onSelectLesson(lesson.id)}
                >
                  {completed ? "Practice Again" : unlocked ? "Start Lesson" : "Complete Previous Lesson"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
